import z from 'zod';
import { zDateField } from '../utils/date.utils.js';
import { purchaseLineShape, refinePurchaseLine, signedUnitPriceSchema, } from './purchase-lines.types.js';
import { isProformaChargeAccountCode, refineCreditNoteImputation, SUPPLIER_PAYMENT_INSTRUMENTS, } from './supplier-invoices.types.js';
export const PURCHASE_PAYMENT_PROFORMA_ERROR = 'Un achat réglé demande un compte de charge réel (classe 6) sur chaque ligne';
export const purchaseLineDataSchema = z
    .object({
    ...purchaseLineShape,
    unitPrice: signedUnitPriceSchema,
    // Omitted: the supplier's default charge account, else the 471 suspense account.
    chargeAccountCode: z.string().min(1).optional(),
    // The invoice lines this line was built from, as keys of the extraction: what the matching
    // learns from once the purchase is saved.
    sourceKeys: z.array(z.string().min(1)).default([]),
    // A line deducting an avoir lives on the invoice alone: the order keeps what was bought.
    imputedCreditNoteId: z.number().int().positive().nullable().default(null),
})
    .superRefine((line, ctx) => {
    refinePurchaseLine(line, ctx);
    refineCreditNoteImputation(line, ctx);
});
export const purchasePaymentSchema = z.object({
    paymentDate: z.coerce.date({ message: 'La date du règlement est requise' }),
    bankAccountCode: z.string().min(1, 'Le compte de décaissement est requis'),
    instrument: z.enum(SUPPLIER_PAYMENT_INSTRUMENTS, {
        message: 'Le mode de règlement est requis',
    }),
    instrumentNumber: z
        .union([z.string().min(1), z.literal('').transform(() => undefined)])
        .optional(),
    amount: z
        .number()
        .positive({ message: 'Le montant doit être positif' })
        .multipleOf(0.01, { message: 'Le montant ne peut pas avoir plus de deux décimales' }),
});
function refinePayment(data, ctx) {
    if (!data.payment)
        return;
    data.lines.forEach((line, index) => {
        if (line.imputedCreditNoteId === null && isProformaChargeAccountCode(line.chargeAccountCode)) {
            ctx.addIssue({
                code: 'custom',
                message: PURCHASE_PAYMENT_PROFORMA_ERROR,
                path: ['lines', index, 'chargeAccountCode'],
            });
        }
    });
}
export const PURCHASE_ONLY_IMPUTATIONS_ERROR = "Au moins une ligne doit porter sur l'achat lui-même";
function refineLines(lines, ctx) {
    if (lines?.every((line) => line.imputedCreditNoteId !== null)) {
        ctx.addIssue({ code: 'custom', message: PURCHASE_ONLY_IMPUTATIONS_ERROR, path: ['lines'] });
    }
}
const receivedInvoiceShape = {
    reference: z.string().trim().min(1, 'La référence de la facture est requise'),
    issueDate: zDateField("La date d'émission est requise"),
    dueDate: zDateField("La date d'échéance est requise"),
};
export const purchaseDataSchema = z
    .object({
    supplierId: z.number().int().positive({ message: 'Le fournisseur est requis' }),
    reference: z.string().trim().min(1, 'La référence est requise'),
    date: z.coerce.date({ message: 'La date est requise' }),
    remarks: z.string().optional().nullable(),
    lines: z.array(purchaseLineDataSchema).min(1, 'Au moins une ligne est requise'),
    // « J'ai reçu les produits »: the order is validated, so its prestations enter the stock.
    productsReceived: z.boolean().default(false),
    // Null while the supplier's document has not arrived: the invoice the order generates then
    // takes the order's reference and date.
    invoice: z.object(receivedInvoiceShape).nullable().default(null),
    importId: z.number().int().positive().nullable().default(null),
    payment: purchasePaymentSchema.nullable().default(null),
})
    .superRefine((data, ctx) => {
    refineLines(data.lines, ctx);
    refinePayment(data, ctx);
});
// The supplier's document arriving for an order whose invoice was generated without it.
export const receivePurchaseInvoiceSchema = z
    .object({
    ...receivedInvoiceShape,
    // Omitted: the lines stay as the order and its invoice have them. Sent, they are the whole
    // invoice: its avoir imputations included.
    lines: z.array(purchaseLineDataSchema).min(1, 'Au moins une ligne est requise').optional(),
    productsReceived: z.boolean().optional(),
    importId: z.number().int().positive().nullable().default(null),
    payment: purchasePaymentSchema.nullable().default(null),
})
    .superRefine((data, ctx) => {
    refineLines(data.lines, ctx);
    refinePayment({ ...data, lines: data.lines ?? [] }, ctx);
});
// An avoir goes through no order: later invoices of its supplier deduct it.
export const creditNoteDataSchema = z.object({
    supplierId: z.number().int().positive({ message: 'Le fournisseur est requis' }),
    reference: z.string().trim().min(1, "Le numéro de l'avoir est requis"),
    issueDate: zDateField("La date d'émission est requise"),
    dueDate: zDateField("La date d'échéance est requise"),
    lines: z.array(purchaseLineDataSchema).min(1, 'Au moins une ligne est requise'),
    importId: z.number().int().positive().nullable().default(null),
});
export const purchaseDuplicatesQuerySchema = z.object({
    supplierId: z.coerce.number().int().positive(),
    reference: z.string().trim().min(1),
    excludeInvoiceId: z.coerce.number().int().positive().optional(),
});
export const PDF_OR_IMAGE_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
export const INVOICE_IMPORT_MAX_SIZE = 8 * 1024 * 1024;
